import { useState, useCallback } from 'react';
// eslint-disable-next-line jira/restricted/use-debounce
import { useDebouncedCallback } from 'use-debounce';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import type { WorkspaceId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { EMPTY_VALIDATED_IQL } from '../common/constants.tsx';
import type { IqlValidationResult, ValidatedIql } from '../common/types.tsx';
import { validateQlQuery } from '../services/index.tsx';

type State = {
	unvalidatedIql: string;
	validationResult: IqlValidationResult;
};
type IqlValidation = [
	State,
	{
		onChange: (newUnvalidatedIql: string) => void;
	},
];

const IQL_VALIDATE_DELAY = 300;

const getInitialStateForIql = (newUnvalidatedIql: string): State => ({
	unvalidatedIql: newUnvalidatedIql,
	validationResult:
		newUnvalidatedIql === EMPTY_VALIDATED_IQL
			? { type: 'valid', validatedIql: EMPTY_VALIDATED_IQL }
			: { type: 'loading' },
});

/**
 * Provides state management for an IQL query input, validating it after changes.
 * The provided initialIql param is expected to already be validated. Use EMPTY_VALIDATED_IQL if you don't have a
 * validated IQL query to provide.
 */
export const useIqlValidation = ({
	workspaceId,
	initialIql,
}: {
	workspaceId: WorkspaceId | undefined;
	initialIql: ValidatedIql;
}): IqlValidation => {
	const [state, setState] = useState<State>({
		unvalidatedIql: String(initialIql),
		validationResult: { type: 'valid', validatedIql: initialIql },
	});

	// Debounce calls to prevent validation being requested on every key press
	const [debouncedValidate] = useDebouncedCallback(async (newUnvalidatedIql: string) => {
		let newValidationResult: IqlValidationResult;
		try {
			if (!workspaceId) {
				return;
			}
			newValidationResult = await validateQlQuery({
				workspaceId,
				unvalidatedIql: newUnvalidatedIql,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireCmdbErrorAnalytics({
				error,
				meta: {
					id: 'validateIql',
					packageName: 'jiraServicedeskInsightIqlValidation',
					teamName: 'aurum',
				},
			});
			newValidationResult = {
				type: 'invalid',
				error: error.message,
			};
		}
		setState((prevState) => {
			// If unvalidatedIql in the state has changed since this was called, this means the user has changed the IQL
			// making this current request invalid.
			if (prevState.unvalidatedIql !== newUnvalidatedIql) {
				return prevState;
			}

			return {
				unvalidatedIql: newUnvalidatedIql,
				validationResult: newValidationResult,
			};
		});
	}, IQL_VALIDATE_DELAY);

	const onChange = useCallback(
		(newUnvalidatedIql: string) => {
			const newState = getInitialStateForIql(newUnvalidatedIql);
			setState(newState);
			if (newState.validationResult.type === 'valid') {
				return;
			}
			debouncedValidate(newUnvalidatedIql);
		},
		[debouncedValidate, setState],
	);

	return [state, { onChange }];
};
